/**
 * WizardButtons.js
 *
 * Provides the "Back", "Next", and "Finish" buttons for the onboarding wizard.
 * These buttons control the step-by-step navigation of the user setup process.
 */
import { useDebug } from '@/hooks/useDebug';
import { useOnboarding } from '@/pages/setup/account/OnboardingContext';
import { useUser } from '@lore/auth';
import { useQueryMutation } from '@lore/query-connect';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import React, { useCallback } from 'react';

/**
 * Renders the navigation buttons (Back, Next, Finish) for the wizard.
 *
 * Includes a debug mode display for viewing the collected onboarding data.
 *
 * @returns {JSX.Element} The rendered wizard buttons.
 */
export default function WizardButtons() {
  const { isFirst, isLast, goBack, goNext, nextDisabled, data } = useOnboarding();
  const [debug, _setDebug] = useDebug()
  const currentUser = useUser();
  const userMutation = useQueryMutation('currentUser.updateSingleton', currentUser);
  const queryClient = useQueryClient();

  const handleFinish = useCallback(async () => {
    const currentTags = currentUser.data.customTags ? currentUser.data.customTags.split(',') : [];
    if (!currentTags.includes('_csAccountCreated')) {
      currentTags.push('_csAccountCreated');
      await userMutation.mutateAsync({ customTags: currentTags.join(',') });
      await queryClient.invalidateQueries(['currentUser'])
    }
    await goNext();
  }, [currentUser.data.customTags, userMutation.mutateAsync, queryClient, goNext]);

  return (
    <>
      {debug && <details className="absolute left-0 text-xs z-50">
        <summary className="cursor-pointer">&#127857;</summary>
        <pre className="bg-cs-blue-100">{JSON.stringify(data, null, 2)}</pre>
      </details>}
      <div className="sticky p-4 z-10 bottom-0 bg-white flex justify-between items-center w-full mt-auto">
        { !isFirst && (<div>
          <button className="btn btn-tertiary disabled:opacity-50 disabled:cursor-not-allowed"
                               onClick={goBack}>Back</button>
        </div>
          )}
        <div className={classNames(isFirst ? 'flex-1' : '')}>
          {!isLast && <button className={classNames("btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed",
            isFirst ? "!w-full justify-center" : "")}
                              disabled={nextDisabled}
                              onClick={goNext}>Next</button>}
          {isLast && <button className="btn btn-primary disabled:opacity-50"
                             disabled={nextDisabled}
                             onClick={handleFinish}>Finish</button>}
        </div>
      </div>
    </>
  )
}
